import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Sparkles } from "lucide-react";
import { Reveal, ProgramIcon } from "./shared";
import { Footer } from "./Footer";
import { PROGRAMS, PROGRAM_DETAILS } from "../../lib/data";
import logoWhite from "../../assets/logo-white-transparent.png";
import a1 from "../../assets/IMG_6558_2.jpg";
import a2 from "../../assets/IMG_6561_2.jpg";
import a3 from "../../assets/IMG_6282.jpg";
import a4 from "../../assets/IMG_6557_2.jpg";
import a5 from "../../assets/IMG_6955.jpg";
import a6 from "../../assets/IMG_6077.jpg";

const IMG = {
  "IMG_6558_2.jpg": a1, "IMG_6561_2.jpg": a2, "IMG_6282.jpg": a3,
  "IMG_6557_2.jpg": a4, "IMG_6955.jpg": a5, "IMG_6077.jpg": a6,
};

export const ProgramDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const meta = PROGRAMS.find((p) => p.slug === slug);
  const d = PROGRAM_DETAILS[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!meta || !d) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", gap: 18, background: "var(--bg-alt)" }}>
        <h2 className="section-title">Program not found</h2>
        <Link to="/" className="btn btn-gold">Back to home</Link>
      </div>
    );
  }

  const idx = PROGRAMS.findIndex((p) => p.slug === slug);
  const next = PROGRAMS[(idx + 1) % PROGRAMS.length];
  const goTrial = () => navigate("/#contact");

  return (
    <div data-testid="program-detail" style={{ background: "var(--bg)" }}>
      {/* Mini header */}
      <header style={{ position: "sticky", top: 0, zIndex: 1000, background: "rgba(14,14,14,0.85)",
        backdropFilter: "blur(14px)", padding: "14px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="container" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Link to="/" data-testid="detail-logo" style={{ display: "flex", alignItems: "center", gap: 10, color: "#fff",
            fontFamily: "'Bebas Neue'", fontSize: 24, letterSpacing: "0.08em" }}>
            <img src={logoWhite} alt="VLA" style={{ width: 40, height: 40, objectFit: "contain" }} /> VLA
          </Link>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Link to="/#programs" className="btn btn-outline" style={{ padding: "10px 20px", fontSize: 12 }}>All Programs</Link>
            <button onClick={goTrial} className="btn btn-gold" data-testid="detail-trial-top" style={{ padding: "10px 20px", fontSize: 12 }}>Book Free Trial</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section style={{ position: "relative", minHeight: "62vh", display: "flex", alignItems: "flex-end",
        color: "#fff", overflow: "hidden" }}>
        <motion.img src={IMG[d.image]} alt={meta.title} initial={{ scale: 1.12 }} animate={{ scale: 1 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 0 }} />
        <div style={{ position: "absolute", inset: 0, zIndex: 1,
          background: "linear-gradient(180deg,rgba(14,14,14,0.5) 0%,rgba(14,14,14,0.55) 40%,rgba(14,14,14,0.94) 100%)" }} />
        <div className="container" style={{ position: "relative", zIndex: 2, paddingBottom: 56, paddingTop: 80 }}>
          <Link to="/#programs" style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "var(--gold)",
            fontSize: 12.5, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, marginBottom: 20 }}>
            <ArrowLeft size={15} /> Programs
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
            <span style={{ width: 64, height: 64, borderRadius: 18, background: "rgba(242,169,59,0.18)",
              border: "1px solid rgba(242,169,59,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}
              className="detail-hero-icon">
              <ProgramIcon name={d.icon} />
            </span>
            <div>
              <p style={{ color: "var(--gold)", fontSize: 13, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 700 }}>{d.tagline}</p>
              <h1 style={{ fontSize: "clamp(48px,8vw,96px)", textTransform: "uppercase", lineHeight: 0.92, color: "#fff" }}>{meta.title}</h1>
            </div>
          </div>
        </div>
      </section>

      {/* Intro + everyone banner */}
      <section className="section" style={{ paddingTop: 80, paddingBottom: 40 }}>
        <div className="container" style={{ maxWidth: 900 }}>
          <Reveal><p style={{ fontSize: "clamp(18px,2.4vw,24px)", color: "var(--text)", lineHeight: 1.6 }}>{d.intro}</p></Reveal>
          <Reveal delay={0.1}>
            <div style={{ marginTop: 34, display: "flex", gap: 16, alignItems: "center", background: "var(--black)",
              color: "#fff", borderRadius: "var(--radius-lg)", padding: "24px 28px" }}>
              <Sparkles size={26} style={{ color: "var(--gold)", flex: "none" }} />
              <p style={{ fontSize: 16.5, margin: 0 }}><strong style={{ color: "var(--gold)" }}>Built for everyone. </strong>{d.forEveryone}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Benefits */}
      <section className="section alt" style={{ paddingTop: 70, paddingBottom: 70 }}>
        <div className="container">
          <Reveal><p className="eyebrow">Why train this</p></Reveal>
          <Reveal delay={0.05}><h2 className="section-title" style={{ marginTop: 14 }}>The <span className="accent">benefits</span></h2></Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 22, marginTop: 44 }} className="why-grid">
            {d.benefits.map((b, i) => (
              <Reveal key={b.t} delay={(i % 2) * 0.08}>
                <div className="why-item" data-testid={`benefit-${i}`}>
                  <span style={{ display: "inline-block", width: 24, height: 5, borderRadius: 3, marginBottom: 14, background: "var(--gold)" }} />
                  <h4>{b.t}</h4>
                  <p>{b.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Who for + What you'll learn */}
      <section className="section" style={{ paddingTop: 70, paddingBottom: 70 }}>
        <div className="container about-grid" style={{ display: "grid", gridTemplateColumns: "0.9fr 1.1fr", gap: 60 }}>
          <Reveal>
            <p className="eyebrow">Who is it for</p>
            <h2 className="section-title" style={{ marginTop: 14, marginBottom: 24 }}>Yes, <span className="accent">you.</span></h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {d.whoFor.map((w) => (
                <span key={w} style={{ padding: "12px 20px", borderRadius: 100, background: "var(--bg-alt)",
                  border: "1px solid rgba(17,17,17,0.08)", fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{w}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="eyebrow">In your first months</p>
            <h2 className="section-title" style={{ marginTop: 14, marginBottom: 24 }}>What you'll <span className="accent">learn</span></h2>
            <ul style={{ display: "grid", gap: 16 }}>
              {d.learn.map((l) => (
                <li key={l} style={{ display: "flex", gap: 14, alignItems: "flex-start", fontSize: 15.5, color: "var(--text)" }}>
                  <span style={{ width: 26, height: 26, borderRadius: "50%", background: "var(--red)", flex: "none",
                    display: "flex", alignItems: "center", justifyContent: "center" }}><Check size={15} color="#fff" /></span>
                  {l}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* CTA band */}
      <section className="section" style={{ paddingTop: 0, paddingBottom: 100 }}>
        <div className="container">
          <div style={{ position: "relative", overflow: "hidden", borderRadius: "var(--radius-lg)",
            background: "linear-gradient(135deg,var(--red),var(--red-dark))", color: "#fff",
            padding: "56px 48px", boxShadow: "var(--shadow-strong)" }}>
            <div style={{ position: "absolute", top: -80, right: -60, width: 260, height: 260,
              background: "radial-gradient(circle,rgba(242,169,59,0.3),transparent 70%)" }} />
            <div style={{ position: "relative", zIndex: 1, display: "flex", justifyContent: "space-between",
              alignItems: "center", gap: 28, flexWrap: "wrap" }}>
              <div>
                <h2 style={{ fontSize: "clamp(32px,4.4vw,52px)", textTransform: "uppercase", color: "#fff" }}>
                  Try {meta.title} <span style={{ color: "var(--gold)" }}>free.</span>
                </h2>
                <p style={{ fontSize: 16, color: "rgba(255,255,255,0.85)", marginTop: 8 }}>
                  Book a no-obligation trial class and feel the difference for yourself.
                </p>
              </div>
              <button onClick={goTrial} className="btn btn-gold" data-testid="detail-trial-cta">
                Book Free Trial <ArrowRight size={17} />
              </button>
            </div>
          </div>

          <Reveal>
            <Link to={`/programs/${next.slug}`} data-testid="next-program"
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 30,
                padding: "24px 28px", borderRadius: "var(--radius-md)", border: "1px solid rgba(17,17,17,0.1)",
                background: "var(--bg-alt)" }}>
              <span style={{ fontSize: 12.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)" }}>Next program</span>
              <span style={{ display: "flex", alignItems: "center", gap: 12, fontFamily: "'Bebas Neue'", fontSize: 28, color: "var(--black)" }}>
                {next.title} <ArrowRight size={20} />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      <Footer onNav={() => navigate("/")} />
    </div>
  );
};
